import React, { useMemo, useState } from 'react';

interface PriceHistoryChartProps {
    data: number[];
    color?: string;
    height?: number;
    className?: string;
}

export const PriceHistoryChart = ({
    data,
    color = '#10b981', // emerald-500
    height = 200,
    className = ''
}: PriceHistoryChartProps) => {
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);

    // Normalize data
    const points = useMemo(() => {
        if (!data || data.length === 0) return [];
        const min = Math.min(...data);
        const max = Math.max(...data);
        const range = max - min || 1; // avoid divide by zero

        // Add 10% padding to top/bottom
        const paddedMin = min - (range * 0.1);
        const paddedMax = max + (range * 0.1);
        const paddedRange = paddedMax - paddedMin;

        return data.map((val, i) => {
            const x = (i / (data.length - 1)) * 100;
            const y = 100 - ((val - paddedMin) / paddedRange) * 100;
            return { x, y, val };
        });
    }, [data]);

    // Create Path
    const pathD = useMemo(() => {
        if (points.length < 2) return '';
        const first = points[0]!;
        let d = `M ${first.x} ${first.y}`;

        // Simple straight lines for now, or use bezier?
        // Let's do simple lines for price accuracy visual
        for (let i = 1; i < points.length; i++) {
            const p = points[i]!;
            d += ` L ${p.x} ${p.y}`;
        }
        return d;
    }, [points]);

    const areaD = useMemo(() => {
        if (!pathD) return '';
        // Close the path to the bottom
        return `${pathD} L 100 100 L 0 100 Z`;
    }, [pathD]);

    if (data.length < 2) {
        return (
            <div className={`chart-not-enough-data ${className}`} style={{ height }}>
                Not enough data to display chart.
            </div>
        );
    }

    return (
        <div className={`chart-container-inner ${className}`} style={{ height }}
            onMouseLeave={() => setHoverIndex(null)}>
            <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                className="chart-svg"
            >
                {/* Gradient Definition */}
                <defs>
                    <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity="0.2" />
                        <stop offset="100%" stopColor={color} stopOpacity="0" />
                    </linearGradient>
                </defs>

                {/* Area Fill */}
                <path d={areaD} fill="url(#chartGradient)" />

                {/* Stroke Line */}
                <path
                    d={pathD}
                    fill="none"
                    stroke={color}
                    strokeWidth="2"
                    vectorEffect="non-scaling-stroke"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />

                {/* Hover Interaction Areas - invisible bars */}
                {points.map((p, i) => (
                    <rect
                        key={i}
                        x={i === 0 ? 0 : points[i - 1]!.x + (p.x - points[i - 1]!.x) / 2}
                        y="0"
                        width={100 / (points.length - 1)} // rough width
                        height="100"
                        fill="transparent"
                        onMouseEnter={() => setHoverIndex(i)}
                    />
                ))}

                {/* Active Hover Point */}
                {hoverIndex !== null && points[hoverIndex] && (
                    <g>
                        <line
                            x1={points[hoverIndex]!.x} y1="0"
                            x2={points[hoverIndex]!.x} y2="100"
                            stroke="rgba(255,255,255,0.2)"
                            strokeWidth="1"
                            vectorEffect="non-scaling-stroke"
                            strokeDasharray="4 4"
                        />
                        <circle
                            cx={points[hoverIndex]!.x}
                            cy={points[hoverIndex]!.y}
                            r="4"
                            fill={color}
                            stroke="var(--color-bg-app)"
                            strokeWidth="2"
                            vectorEffect="non-scaling-stroke"
                        />
                    </g>
                )}
            </svg>

            {/* Tooltip Overlay */}
            {hoverIndex !== null && points[hoverIndex] && (
                <div
                    className="chart-tooltip"
                    style={{
                        left: `${points[hoverIndex]!.x}%`,
                        top: `${points[hoverIndex]!.y}%`,
                        marginTop: '-36px'
                    }}
                >
                    <span style={{ fontFamily: 'monospace' }}>
                        {points[hoverIndex]!.val.toLocaleString()} G
                    </span>
                    <div className="chart-tooltip-arrow"></div>
                </div>
            )}
        </div>
    );
};
